import React, { useEffect, useState } from 'react';
import { Plus, Clock, FileText, Trash2, Edit, Layers, Loader2, User as UserIcon } from 'lucide-react';
import { getStories, deleteStory, getUsers } from '../services/store'; // Added getUsers
import { Story, User } from '../types';
import { LANGUAGE_LABELS } from '../constants';
import { supabase } from '../services/supabaseClient';

interface DashboardProps {
  onEditStory: (id: string) => void;
  onNewStory: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onEditStory, onNewStory }) => {
  const [stories, setStories] = useState<Story[]>([]);
  const [userMap, setUserMap] = useState<Record<string, string>>({}); // ID -> Name map
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const [fetchedStories, fetchedUsers] = await Promise.all([
        getStories(),
        getUsers()
      ]);
      
      // Create a quick lookup map for user names
      const map: Record<string, string> = {};
      fetchedUsers.forEach(u => map[u.id] = u.name);
      setUserMap(map);
      
      setStories(fetchedStories);
      setLoading(false);
    };
    fetch();
  }, [refresh]);

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this story?")) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await deleteStory(id, user.id);
        setRefresh(prev => prev + 1);
      }
    }
  };

  const groupedStories = React.useMemo(() => {
    const groups: Record<string, Story[]> = {};
    stories.forEach(story => {
      if (!groups[story.groupId]) groups[story.groupId] = [];
      groups[story.groupId].push(story);
    });
    return Object.values(groups).sort((a, b) => {
      const dateA = new Date(Math.max(...a.map(s => new Date(s.updatedAt).getTime())));
      const dateB = new Date(Math.max(...b.map(s => new Date(s.updatedAt).getTime())));
      return dateB.getTime() - dateA.getTime();
    });
  }, [stories]);

  const getStatusBadge = (status: string) => {
    if (status === 'published' || status === 'stage5') {
      return <span className="px-2.5 py-1 text-xs font-medium rounded-full border bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">Ready</span>;
    }
    if (status === 'draft') {
      return <span className="px-2.5 py-1 text-xs font-medium rounded-full border bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700">Draft</span>;
    }
    return <span className="px-2.5 py-1 text-xs font-medium rounded-full border bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800">In Review</span>;
  };

  if (loading && stories.length === 0) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto h-full overflow-y-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">My Queue</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage your stories and translations</p>
        </div>
        <button
          onClick={onNewStory}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
        >
          <Plus className="h-4 w-4 mr-2" /> New Story
        </button>
      </div>

      {stories.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          <div className="bg-slate-50 dark:bg-slate-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
             <FileText className="h-8 w-8 text-slate-400 dark:text-slate-500" />
          </div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">No stories yet</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">Create your first story to get started.</p>
          <button onClick={onNewStory} className="text-blue-600 dark:text-blue-400 font-medium hover:text-blue-800 transition-colors">Create New Story &rarr;</button>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedStories.map((group, groupIdx) => (
            <div key={groupIdx} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
               {group.length > 1 && (
                 <div className="bg-slate-50 dark:bg-slate-800/80 px-6 py-2 border-b border-slate-200 dark:border-slate-800 flex items-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                   <Layers className="h-3 w-3 mr-2" /> Story Group {group[0].groupId.slice(0, 8)}
                 </div>
               )}
               <div className="divide-y divide-slate-100 dark:divide-slate-800">
                 {group.map((story) => (
                   <div 
                    key={story.id}
                    onClick={() => onEditStory(story.id)}
                    className="flex items-center p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group"
                   >
                      <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 mr-4">
                        <FileText className="h-5 w-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0 mr-6">
                        <div className="flex items-center mb-1">
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate group-hover:text-blue-600 transition-colors">
                            {story.headline || "Untitled Draft"}
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 space-x-3">
                           {/* Added Author Name */}
                           <span className="flex items-center font-medium text-slate-700 dark:text-slate-300">
                             <UserIcon className="h-3 w-3 mr-1" /> {userMap[story.authorId] || 'Unknown'}
                           </span>
                           <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></span>
                           <span>{LANGUAGE_LABELS[story.language]}</span>
                           <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></span>
                           <span className="flex items-center">
                             <Clock className="h-3 w-3 mr-1" />
                             {new Date(story.updatedAt).toLocaleDateString()}
                           </span>
                        </div>
                      </div>

                      <div className="mr-6">{getStatusBadge(story.status)}</div>

                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                         <button onClick={(e) => { e.stopPropagation(); onEditStory(story.id); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-full mr-1"><Edit className="h-4 w-4" /></button>
                         <button onClick={(e) => handleDelete(story.id, e)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full"><Trash2 className="h-4 w-4" /></button>
                      </div>
                   </div>
                 ))}
               </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;